import { setApiBaseUrl } from '@graal/api';
import { createQueryClient } from '@/lib/query-client';
import '@graal/tokens/fonts.css';
import './styles/app.css';
import './i18n';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { MotionConfig } from 'motion/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AppErrorBoundary, RouteErrorComponent } from '@/components/shell/error-boundary';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { runtimeEnv } from '@/lib/env';
import { applyStoredTheme, ThemeProvider } from '@/theme/theme-provider';
import { routeTree } from './routeTree.gen';

// Avant le premier rendu : <html data-theme data-mode> est posé ici et nulle part ailleurs.
// index.html ne fige aucun thème — changer DEFAULT_THEME dans packages/tokens suffit.
applyStoredTheme();

setApiBaseUrl(runtimeEnv().apiUrl);

const queryClient = createQueryClient();

// Toute route qui lève (rendu, beforeLoad, loader) affiche `RouteErrorComponent` : message traduit
// et référence de corrélation de l'API quand elle existe. `AppErrorBoundary` attrape le reste.
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultErrorComponent: RouteErrorComponent,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root');
if (!container) throw new Error('#root introuvable dans index.html');

function render() {
  createRoot(container!).render(
    <StrictMode>
      <ThemeProvider>
        {/* reducedMotion="user" : les animations 120–180 ms sont coupées si l'OS le demande. */}
        <MotionConfig reducedMotion="user">
          <QueryClientProvider client={queryClient}>
            <TooltipProvider delayDuration={200}>
              <AppErrorBoundary>
                <RouterProvider router={router} />
              </AppErrorBoundary>
              <Toaster position="bottom-right" />
            </TooltipProvider>
          </QueryClientProvider>
        </MotionConfig>
      </ThemeProvider>
    </StrictMode>,
  );
}

// Les mocks MSW doivent être prêts AVANT le premier rendu : sinon la première requête du
// routeur (lookup tenant → realm) part sur le réseau et échoue. Import dynamique : rien de
// tout cela n'entre dans le bundle principal quand GRAAL_MOCKS n'est pas posé.
if (runtimeEnv().mocks) {
  void import('./mocks/start').then(({ startMocks }) => startMocks()).then(render, render);
} else {
  render();
}
